// @lub0v/santa-utils validators are built with emitDecoratorMetadata, so the
// metadata shim has to be in place before any of its classes are evaluated.
import 'reflect-metadata';
import { prepareHomeEntrance, changeHomeAppearance } from '@src/theme/homeMotion';

import { swallowNotificationAccessTokenFromUrl } from '@src/access/notificationAccessToken';
import { StrictMode, useLayoutEffect, type ReactNode } from 'react';
import { currentBoxSubdomain } from '@src/routing/boxDomain';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { AppTree } from '@src/AppTree';
import i18n, { initI18n } from '@src/i18n';
import { siteLanguage } from '@src/site';
import { Lang } from '@src/i18n/lang';
import { updateMetaTags } from '@src/seo/meta';
import { applyTheme, resolveInitialTheme } from '@src/theme/applyTheme';
import { installVersionFlagHelper, reconcileVersionFlag } from '@src/versionFlag';
import '@src/styles/index.css';
import { preloadLandscape } from '@src/theme/preloadLandscape';
import { loadSeason } from '@src/theme/registry';
import { store } from '@src/state/store';
import { loadSession } from '@src/state/sessionSlice';
import { resolveSiteSeason } from '@src/theme/siteSeason';
import { currentLocalSeasonPreview } from '@src/theme/localSeasonPreview';
import { isSeason } from '@src/theme/season';

/** Reveal only after React has committed the selected package and its styles. */
function RevealSeason({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    // Hydrate in the baked language first, then restore the visitor's choice.
    // Static .ru HTML is Russian even when the browser has an English cookie.
    const language = siteLanguage();
    void Promise.all([i18n.changeLanguage(language), document.fonts.ready]).then(() => {
      document.documentElement.lang = language;
      updateMetaTags();
      delete document.documentElement.dataset.seasonPending;
      document.documentElement.dataset.appReady = 'true';
    });
  }, []);
  return children;
}

swallowNotificationAccessTokenFromUrl();
installVersionFlagHelper();

// The browser selected the old version: a reload into v1 is already
// on its way, so don't bother mounting.
if (!reconcileVersionFlag()) {
  const container = document.getElementById('root');
  if (!container) {
    throw new Error('#root is missing from the document');
  }

  // React 19 SSR leaves image preloads as siblings inside #root. Drop them
  // so hydrateRoot sees the same tree the client will render.
  container.querySelectorAll(':scope > link[rel="preload"]').forEach((node) => node.remove());
  const hasServerMarkup =
    !currentBoxSubdomain() && Boolean(container.querySelector(':scope > .app'));
  const htmlLang = document.documentElement.lang;
  const ssrLang = htmlLang === Lang.EN || htmlLang === Lang.RU ? htmlLang : undefined;
  // Language from the baked HTML so hydrate matches; theme from storage so it
  // matches the blocking script in index.html (no light→dark flip).
  void initI18n(hasServerMarkup ? ssrLang : undefined)
    .then(async () => {
      updateMetaTags();

      const initialTheme = resolveInitialTheme();
      const publicHome = location.pathname === '/' && !currentBoxSubdomain();
      // Keep baked Home's color/image pairing until React commits. Its session
      // starts in ProductApp after mounting, independently of the scenery.
      if (!publicHome) applyTheme(initialTheme);

      const serverSeason = container
        .querySelector('[data-season-styles]')
        ?.getAttribute('data-season-styles');
      // Other routes resolve identity before choosing artwork. Public Home
      // mounts its assigned default and restores saved appearance in UserAppearance.
      if (!publicHome && location.pathname !== '/theme-preview') await store.dispatch(loadSession());
      const session = store.getState().session;
      const baseSeason = !publicHome && session.status === 'ready'
        ? resolveSiteSeason(session.user?.theme) : resolveSiteSeason();
      const previewSeason = location.pathname === '/theme-preview'
        ? new URLSearchParams(location.search).get('season') : null;
      const season = isSeason(previewSeason) ? previewSeason : currentLocalSeasonPreview() ?? baseSeason;
      // Static HTML includes mode-specific image URLs, not just CSS colors.
      // React does not repair mismatched src attributes during hydration.
      const serverTheme = document.documentElement.dataset.renderedTheme ?? 'light';
      const hydrating =
        hasServerMarkup && !session.user && season === serverSeason && initialTheme === serverTheme;
      const seasonPackage = await loadSeason(season);
      // The preview receives its day/night mode from its parent before rendering;
      // its own asset gate decodes that mode without preloading an unused one.
      if (location.pathname !== '/theme-preview') await preloadLandscape(seasonPackage, initialTheme);
      const tree = (
        <RevealSeason>
          <AppTree theme={initialTheme} seasonPackage={seasonPackage} />
        </RevealSeason>
      );
      if (hydrating) {
        // StrictMode remounts in dev and would undo the hydrate (same blink).
        hydrateRoot(container, tree);
      } else {
        await changeHomeAppearance(() => {
          if (publicHome) prepareHomeEntrance(hasServerMarkup && document.documentElement.dataset.homepageVisible === 'true');
          container.replaceChildren();
          createRoot(container).render(<StrictMode>{tree}</StrictMode>);
        });
      }
    })
    .catch((error: unknown) => {
      // Keep the failed season hidden; provide a retry instead of flashing winter.
      console.error('Unable to start the selected theme', error);
      const retry = document.createElement('a');
      retry.href = window.location.href;
      retry.textContent =
        htmlLang === 'ru'
          ? 'Не удалось загрузить тему. Перезагрузить'
          : 'Unable to load the theme. Reload';
      container.style.removeProperty('opacity');
      container.replaceChildren(retry);
      delete document.documentElement.dataset.seasonPending;
    });
}
